/**
 * Product-page checkbox that adds an extra product (gift bag, shipping
 * insurance) to the cart.
 *
 * The checkbox always shows ticked when the add-on is already in the cart, on
 * every product page, so it never contradicts what the shopper is paying for.
 *
 * Two modes decide the starting state otherwise:
 *   immediate  - starts unchecked. Ticking adds the add-on right away.
 *   default_on - starts ticked when the cart is empty, since there is nothing
 *                to attach the add-on to yet; it goes in once a product does.
 *
 * Declining is remembered for the session, so an add-on the shopper removed
 * never creeps back when they browse to the next product.
 *
 * In immediate mode nothing is bound to the product form at all. Easify
 * Options hooks that form for personalization, and breaking it would be far
 * worse than losing an add-on.
 */
if (!customElements.get('cart-addon-option')) {
  const root = () => window.Shopify?.routes?.root || '/';

  /**
   * Shopify rejects concurrent writes to the same cart, so every mutation from
   * this component goes through one queue. Without it, two add-ons reacting to
   * the same add-to-cart click race each other and one silently loses.
   */
  let cartQueue = Promise.resolve();
  const queueCartWrite = (task) => {
    const result = cartQueue.then(task, task);
    cartQueue = result.then(
      () => undefined,
      () => undefined
    );
    return result;
  };

  class CartAddonOption extends HTMLElement {
    constructor() {
      super();
      this.variantId = Number(this.dataset.variantId);
      this.mode = this.dataset.mode || 'immediate';
      // 'order': one for the whole order (insurance). 'item': one per
      // product the shopper opts in on (a gift bag for each gift).
      this.per = this.dataset.per || 'order';
      this.addedHere = false;
      this.storageKey = 'everlles:addon-declined:' + this.dataset.handle;
      this.input = this.querySelector('.cart-addon__input');
      this.status = this.querySelector('.cart-addon__status');
      this.busy = false;
      this.armed = false;
      this.watching = false;
    }

    connectedCallback() {
      if (!this.input || !this.variantId) return;
      // Browsers restore checkbox state across soft reloads; decide it ourselves.
      this.input.checked = false;
      this.input.addEventListener('change', () => this.onToggle());
      this.syncFromCart();
    }

    /**
     * Decide the checkbox's starting state from the cart.
     *
     * If the add-on is already in the order the box shows ticked on every
     * product page — an empty box beside something the shopper is already
     * paying for would be a lie. Beyond that, only `default_on` opts in by
     * default, and only for an empty cart.
     */
    async syncFromCart() {
      let cart;
      try {
        cart = await this.getCart();
      } catch (_) {
        return; // leave unchecked rather than guess
      }

      if (this.lineFor(cart)) {
        this.input.checked = true;
        this.setStatus(this.dataset.textAdded);
        return;
      }
      if (this.mode !== 'default_on' || this.declined) return;

      if (this.otherItemCount(cart) === 0) {
        // Empty cart: default to opted in, and add it once a product goes in.
        this.input.checked = true;
        this.armed = true;
        this.setStatus(this.dataset.textPending);
        this.watchForProduct();
      }
      // Cart has items but no add-on: the shopper passed on it. Leave it off.
    }

    get declined() {
      try {
        return sessionStorage.getItem(this.storageKey) === '1';
      } catch (_) {
        return false;
      }
    }

    set declined(value) {
      try {
        if (value) sessionStorage.setItem(this.storageKey, '1');
        else sessionStorage.removeItem(this.storageKey);
      } catch (_) {
        /* private mode or storage disabled; fall back to per-page behaviour */
      }
    }

    async getCart() {
      const res = await fetch(root() + 'cart.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!res.ok) throw new Error('cart.js ' + res.status);
      return res.json();
    }

    lineFor(cart) {
      return cart.items.find((item) => item.variant_id === this.variantId);
    }

    /** Units in the cart that are not this add-on - i.e. something to insure. */
    otherItemCount(cart) {
      return cart.items
        .filter((item) => item.variant_id !== this.variantId)
        .reduce((sum, item) => sum + item.quantity, 0);
    }

    setStatus(text) {
      if (this.status) this.status.textContent = text || '';
    }

    setBusy(busy) {
      this.busy = busy;
      this.dataset.busy = busy ? 'true' : 'false';
    }

    /**
     * Wait for the shopper to add a product, then put the add-on in beside it.
     * Listens in the capture phase and never calls preventDefault, so the form
     * and anything else hooked to it proceed untouched.
     */
    watchForProduct() {
      if (this.watching) return;
      this.watching = true;
      document.addEventListener(
        'submit',
        (event) => {
          const form = event.target;
          if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
          setTimeout(() => this.onProductAdded(), 0);
        },
        true
      );
    }

    async onProductAdded() {
      if (!this.armed || this.busy || !this.input.checked) return;
      const deadline = Date.now() + 10000;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 400));
        try {
          const cart = await this.getCart();
          if (this.lineFor(cart)) return; // already there
          if (this.otherItemCount(cart) > 0) {
            this.armed = false;
            this.setBusy(true);
            try {
              await this.add();
            } catch (_) {
              // Surface the failure instead of leaving a ticked box and no line.
              this.input.checked = false;
              this.setStatus(this.dataset.textError);
            } finally {
              this.setBusy(false);
            }
            return;
          }
        } catch (_) {
          /* keep waiting until the deadline */
        }
      }
    }

    async onToggle() {
      if (this.busy) return;
      const wanted = this.input.checked;
      this.setBusy(true);
      try {
        if (!wanted) {
          this.armed = false;
          this.declined = true;
          await this.remove();
        } else {
          this.declined = false;
          const cart = await this.getCart();
          // `immediate` always adds on tick. Only `default_on` waits, and only
          // while the cart is empty — there is nothing to insure yet.
          if (this.mode === 'default_on' && this.otherItemCount(cart) === 0) {
            this.armed = true;
            this.setStatus(this.dataset.textPending);
            this.watchForProduct();
          } else {
            await this.add();
          }
        }
      } catch (_) {
        this.input.checked = !wanted; // never let the box lie about the cart
        this.setStatus(this.dataset.textError);
      } finally {
        this.setBusy(false);
      }
    }

    /** Dawn's cart drawer, when this theme has one. */
    get drawer() {
      return document.querySelector('cart-drawer, cart-notification');
    }

    /**
     * Section ids Dawn needs re-rendered after a cart change. Asking the drawer
     * itself keeps us honest if the theme's section names ever change.
     */
    sectionsToRequest() {
      const drawer = this.drawer;
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        try {
          return drawer.getSectionsToRender().map((section) => section.section || section.id);
        } catch (_) {
          /* fall through to the defaults */
        }
      }
      return ['cart-drawer', 'cart-icon-bubble'];
    }

    /** One cart write. Call it only from inside queueCartWrite. */
    async sendCart(path, payload) {
      const body = Object.assign({}, payload, {
        sections: this.sectionsToRequest(),
        sections_url: window.location.pathname,
      });
      const res = await fetch(root() + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(path + ' ' + res.status);
      return res.json();
    }

    postCart(path, payload) {
      return queueCartWrite(() => this.sendCart(path, payload));
    }

    async add() {
      // One-per-order add-ons (insurance) never stack. Per-item ones (a gift
      // bag for each gift) increment, so a second product gets its own.
      // The check and the add run as ONE queued step: checked outside the
      // queue, two triggers firing together both saw "not in cart" and both
      // added, putting insurance in twice.
      const state = await queueCartWrite(async () => {
        if (this.per === 'order' && this.lineFor(await this.getCart())) return null;
        return this.sendCart('cart/add.js', { items: [{ id: this.variantId, quantity: 1 }] });
      });
      if (!state) {
        this.setStatus(this.dataset.textAdded);
        return;
      }
      this.addedHere = true;
      this.setStatus(this.dataset.textAdded);
      // add.js returns the added line, not the cart, so it carries no
      // item_count; we just added something, so the cart is not empty.
      this.notifyCart(state, typeof state.item_count === 'number' ? state.item_count : 1);
    }

    async remove() {
      const cart = await this.getCart();
      const line = this.lineFor(cart);
      if (!line) {
        this.setStatus('');
        return;
      }
      // For a per-item add-on, only take back the one this page put in —
      // otherwise unticking here would remove the bag added for another product.
      if (this.per === 'item' && !this.addedHere) {
        this.setStatus('');
        return;
      }
      const quantity = this.per === 'item' ? Math.max(line.quantity - 1, 0) : 0;
      const state = await this.postCart('cart/change.js', { id: line.key, quantity });
      this.addedHere = false;
      this.setStatus(this.dataset.textRemoved);
      this.notifyCart(state, state.item_count);
    }

    /**
     * Repaint the cart UI from the sections we requested.
     *
     * Dawn re-renders through the Section Rendering API, so plain DOM events
     * are not enough — without this the drawer keeps stale lines and totals.
     * We do the swap ourselves rather than call Dawn's renderContents(), which
     * force-opens the drawer; a shopper ticking a checkbox should not have the
     * cart fly open at them.
     */
    applySections(state, itemCount) {
      const drawer = this.drawer;
      if (!drawer || !state || !state.sections) return false;

      const targets =
        typeof drawer.getSectionsToRender === 'function'
          ? drawer.getSectionsToRender()
          : [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];

      let applied = false;
      targets.forEach((target) => {
        const html = state.sections[target.id];
        if (!html) return;
        const element = target.selector
          ? document.querySelector(target.selector)
          : document.getElementById(target.id);
        if (!element) return;
        const parsed = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector(target.selector || '.shopify-section');
        if (!parsed) return;
        element.innerHTML = parsed.innerHTML;
        applied = true;
      });

      // `is-empty` sits on the outer <cart-drawer>, so swapping the inner HTML
      // above does not clear it.
      if (applied && typeof itemCount === 'number') {
        drawer.classList.toggle('is-empty', itemCount === 0);
        const inner = drawer.querySelector('.drawer__inner');
        if (inner) inner.classList.toggle('is-empty', itemCount === 0);
      }
      return applied;
    }

    notifyCart(state, itemCount) {
      this.applySections(state, itemCount);
      ['cart:refresh', 'cart:build', 'cart:update'].forEach((name) => {
        document.dispatchEvent(new CustomEvent(name, { bubbles: true }));
      });
      if (window.Shopify && typeof window.Shopify.onCartUpdate === 'function') {
        try {
          window.Shopify.onCartUpdate();
        } catch (_) {
          /* app-side handler threw; not ours to fix */
        }
      }
    }
  }

  customElements.define('cart-addon-option', CartAddonOption);
}
